import React from "react";
import { Navigate, useRoutes } from "react-router-dom";
import NotFound from "../components/NotFound";
import LayoutSelector from "./layouts/LayoutSelector";
import { navigationConfig } from "./navigationConfig";
import {
  ROUTE_BROWSE_JOBS_NEW,
  ROUTE_CERTIFICATION_NEW,
  ROUTE_COMPLETE_PROFILE,
  ROUTE_CONNECTOR,
  ROUTE_CRAFTING_VIDEO,
  ROUTE_EDUCATION_NEW,
  ROUTE_EXPERIENCE_NEW,
  ROUTE_FANTASTIC,
  ROUTE_GREATE_JOB_PROFILE,
  ROUTE_GREAT_GOING,
  ROUTE_GREAT_START,
  ROUTE_JOB_CONGRATS,
  ROUTE_JOB_DETAILS,
  ROUTE_PUBLIC_JOB_DETAILS,
  ROUTE_LOOKING_FOR,
  ROUTE_MOBILE_SETTINGS,
  ROUTE_NOTIFICATIONS,
  ROUTE_PERSONALITY_PROFILER,
  ROUTE_PROFILER,
  ROUTE_PROFILE_CONSIST,
  ROUTE_PROMPT,
  ROUTE_QUESTION,
  ROUTE_RECORDING_TIPS,
  ROUTE_REGISTER_COMPLETE,
  ROUTE_SECTION_COMPLETE,
  ROUTE_SECTION_FINAL,
  ROUTE_SIGNIN_NEW,
  ROUTE_SKILLSET_NEW,
  ROUTE_TAKE_TEST,
  ROUTE_UPLOAD_FINAL_VIDEO,
  ROUTE_UPLOAD_PIC,
  ROUTE_VERIFY_MOBILE,
  ROUTE_VIDEO_COMPLETE,
  ROUTE_VIDEO_START,
  ROUTE_WELCOME,
  ROUTE_WELCOME_NEW,
  ROUTE_WELL_DONE,
  ROUTE_WHAT_YOUR_NAME,
} from "../constants/routes";
import WhatYourName from "../components/auth/WhatYourName";
import { Box, useMediaQuery } from "@mui/material";
import JobsCongrats from "../components/browseJobs/JobCongrats";
import MobileSettings from "../components/dashboard/MobileSettings";
import VerifyMobileNumber from "../components/onboarding/VerifyMobileNumber";
import SignInNew from "../components/onboarding/SignInNew";
import Welcome from "../components/onboarding/Welcome";
import WelcomeNew from "../components/onboarding/WelcomeNew";
import UploadPicture from "../components/onboarding/UploadPicture";
import LookingFor from "../components/onboarding/LookingFor";
import GreatStart from "../components/onboarding/GreatStart";
import Fantastic from "../components/onboarding/Fantastic";
import ProfileConsist from "../components/profile/ProfileConsist";
import BrowseJobsNew from "../components/browseJobs/BrowseJobsNew";
import JobDetails from "../components/browseJobs/JobDetails";
import EducationNew from "../components/profile/EducationNew";
import SkillSetNew from "../components/profile/SkillSetNew";
import CraftingVideo from "../components/profile/CraftingVideo";
import GreatJobProfile from "../components/profile/GreatJobProfile";
import CompleteProfile from "../components/profile/CompleteProfile";
import CertificationNew from "../components/profile/CertificationNew";
import ExperienceNew from "../components/profile/ExperienceNew";
import Prompt from "../components/onboarding/Prompt";
import UploadFinalVideo from "../components/onboarding/UploadFinalVideo";
import Profiler from "../components/onboarding/Profiler";
import Question from "../components/onboarding/Question";
import Connector from "../components/onboarding/Connector";
import PersonalityProfiler from "../components/onboarding/PersonalityProfiler";
import RecordingTips from "../components/onboarding/RecordingTips";
import SectionFinal from "../components/onboarding/SectionFinal";
import TakeTest from "../components/onboarding/TakeTest";
import WellDone from "../components/onboarding/WellDone";
import GreatGoing from "../components/onboarding/GreatGoing";
import SectionComplete from "../components/onboarding/SectionComplete";
import VideoStart from "../components/common/VideoStart";
import VideoComplete from "../components/onboarding/VideoComplete";
import RegisterComplete from "../components/b2b/RegisterComplete";
import ClientJobDetails from "../components/b2b/ClientJobDetails";
import ShareJobDetails from "../components/b2b/ShareJobDetails";
import CreateJob from "../components/b2b/CreateJob";
import { ROUTE_VIEW_JOB, ROUTE_CANDIDATE_PROFILE, ROUTE_COMPANY_JOB_DETAILS, ROUTE_PRIVACY_POLICY, ROUTE_TERMS_AND_CONDITIONS } from "../constants/routes";
import PrivacyPolicy from "../components/static/PrivacyPolicy";
import TermsAndConditions from "../components/static/TermsAndConditions";
import NotificationsPage from "../components/notifications/NotificationsPage";

const Routes = ({ userRole }: { userRole: string }): JSX.Element => {
  const isMobileScreen = useMediaQuery("only screen and (max-width: 600px), print", { noSsr: true });

  // Define public routes that don't require authentication
  const publicRoutes = [
    ROUTE_WHAT_YOUR_NAME,
    ROUTE_VERIFY_MOBILE,
    ROUTE_SIGNIN_NEW,
    ROUTE_WELCOME,
    ROUTE_WELCOME_NEW,
    ROUTE_UPLOAD_PIC,
    ROUTE_LOOKING_FOR,
    ROUTE_GREAT_START,
    ROUTE_FANTASTIC,
    ROUTE_PROFILE_CONSIST,
    ROUTE_BROWSE_JOBS_NEW,
    ROUTE_EDUCATION_NEW,
    ROUTE_SKILLSET_NEW,
    ROUTE_CRAFTING_VIDEO,
    ROUTE_GREATE_JOB_PROFILE,
    ROUTE_COMPLETE_PROFILE,
    ROUTE_CERTIFICATION_NEW,
    ROUTE_EXPERIENCE_NEW,
    ROUTE_PROMPT,
    ROUTE_UPLOAD_FINAL_VIDEO,
    ROUTE_PROFILER,
    ROUTE_QUESTION,
    ROUTE_CONNECTOR,
    ROUTE_PERSONALITY_PROFILER,
    ROUTE_RECORDING_TIPS,
    ROUTE_SECTION_COMPLETE,
    ROUTE_SECTION_FINAL,
    ROUTE_GREAT_GOING,
    ROUTE_TAKE_TEST,
    ROUTE_WELL_DONE,
    ROUTE_VIDEO_START,
    ROUTE_VIDEO_COMPLETE,
    ROUTE_REGISTER_COMPLETE,
    ROUTE_MOBILE_SETTINGS,
    ROUTE_JOB_CONGRATS,
    "/shareprofile/:jobId/:profileid",
    ROUTE_CANDIDATE_PROFILE,
    ROUTE_PUBLIC_JOB_DETAILS,
    ROUTE_PRIVACY_POLICY,
    ROUTE_TERMS_AND_CONDITIONS,
    "/404"
  ];

  const routes: React.ReactNode = useRoutes([
    // Public routes (no authentication required)
    {
      path: ROUTE_WHAT_YOUR_NAME,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <WhatYourName />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_MOBILE_SETTINGS,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <MobileSettings />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_JOB_CONGRATS,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <JobsCongrats />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_PRIVACY_POLICY,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <PrivacyPolicy />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_TERMS_AND_CONDITIONS,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <TermsAndConditions />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_VERIFY_MOBILE,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <VerifyMobileNumber />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_SIGNIN_NEW,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <SignInNew />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_WELCOME,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <Welcome />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_WELCOME_NEW,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <WelcomeNew />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_UPLOAD_PIC,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <UploadPicture />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_LOOKING_FOR,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <LookingFor />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_GREAT_START,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <GreatStart />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_FANTASTIC,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <Fantastic />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_PROFILE_CONSIST,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <ProfileConsist />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_BROWSE_JOBS_NEW,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <BrowseJobsNew />
        </LayoutSelector>
      ),
    },
    {
      path: `${ROUTE_JOB_DETAILS}/:id`,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <JobDetails />
        </LayoutSelector>
      ),
    },
    {
      path: `${ROUTE_PUBLIC_JOB_DETAILS}/:id`,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <JobDetails />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_EDUCATION_NEW,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <EducationNew />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_SKILLSET_NEW,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <SkillSetNew />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_CRAFTING_VIDEO,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <CraftingVideo />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_GREATE_JOB_PROFILE,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <GreatJobProfile />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_COMPLETE_PROFILE,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <CompleteProfile />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_NOTIFICATIONS,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <NotificationsPage />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_CERTIFICATION_NEW,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <CertificationNew />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_EXPERIENCE_NEW,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <ExperienceNew />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_PROMPT,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <Prompt />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_UPLOAD_FINAL_VIDEO,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <UploadFinalVideo />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_PROFILER,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <Profiler />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_QUESTION,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <Question />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_CONNECTOR,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <Connector />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_PERSONALITY_PROFILER,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <PersonalityProfiler />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_RECORDING_TIPS,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <RecordingTips />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_SECTION_COMPLETE,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <SectionComplete />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_SECTION_FINAL,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <SectionFinal />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_GREAT_GOING,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <GreatGoing />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_TAKE_TEST,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <TakeTest />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_WELL_DONE,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <WellDone />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_VIDEO_START,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <VideoStart />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_VIDEO_COMPLETE,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <VideoComplete />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_REGISTER_COMPLETE,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <RegisterComplete />
        </LayoutSelector>
      ),
    },
    {
      path: "/shareprofile/:jobId/:profileid",
      element: (
        <LayoutSelector isPublicRoute={true}>
          <ShareJobDetails />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_CANDIDATE_PROFILE,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <ShareJobDetails />
        </LayoutSelector>
      ),
    },
    {
      path: ROUTE_VIEW_JOB,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <CreateJob />
        </LayoutSelector>
      ),
    },
    {
      path: `${ROUTE_VIEW_JOB}/:jobId`,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <CreateJob />
        </LayoutSelector>
      ),
    },
    {
      path: `${ROUTE_COMPANY_JOB_DETAILS}/:jobId`,
      element: (
        <LayoutSelector isPublicRoute={true}>
          <ClientJobDetails />
        </LayoutSelector>
      ),
    },

    // Role-based routes (require authentication)
    ...navigationConfig.map((item) => {
      const { path, roles, component: Component } = item;
      return {
        path,
        element: roles.includes(userRole) ? (
          <LayoutSelector>
            <Component />
          </LayoutSelector>
        ) : (
          <Navigate to="/404" />
        ),
      };
    }),
    
    {
      path: "/404",
      element: (
        <LayoutSelector isPublicRoute={true}>
          <NotFound />
        </LayoutSelector>
      ),
    },
    {
      path: "*",
      element: <Navigate to="/404" />,
    },
  ]);

  return routes as JSX.Element;

  if (isMobileScreen) {
    return routes as JSX.Element;
  }
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        "@media print": {
          display: "none",
        },
      }}
    >
      Please use mobile device to access this page
    </Box>
  );
};

export default Routes;
